import { useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'

export default function Login() {
  const { user, login, loading } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (loading) return <FullPageSpinner />
  if (user) return <Navigate to="/" replace />

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Unexpected error. Try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-fg text-xl font-bold tracking-tight mb-3">
            A
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-fg">Atlas</h1>
          <p className="text-sm text-fg-subtle mt-1">Sign in to continue</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-bg-elevated border border-border rounded-xl shadow-sm p-6 space-y-4"
        >
          {error && (
            <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          )}

          <Field
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            required
            autoComplete="username"
            autoFocus
          />
          <Field
            id="password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            required
            autoComplete="current-password"
          />

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-accent text-accent-fg py-2.5 text-sm font-medium hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-fg-subtle">
          dkproject.diretek.com · v0.1
        </p>
      </div>
    </div>
  )
}

function Field({
  id, label, type, value, onChange, required, autoComplete, autoFocus,
}: {
  id: string
  label: string
  type: string
  value: string
  onChange: (v: string) => void
  required?: boolean
  autoComplete?: string
  autoFocus?: boolean
}) {
  return (
    <label htmlFor={id} className="block">
      <span className="block text-xs font-medium text-fg-muted mb-1.5">{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-disabled focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
      />
    </label>
  )
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="h-8 w-8 rounded-full border-2 border-border border-t-accent animate-spin" />
    </div>
  )
}
